import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CachedIcon from '@mui/icons-material/Cached';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import { useTranslation } from '../common/components/LocalizationProvider';
import useUserAttributes from '../common/attributes/useUserAttributes';
import { sessionActions } from '../store';
import SelectField from '../common/components/SelectField';
import SettingsMenu from './components/SettingsMenu';
import useCommonUserAttributes from '../common/attributes/useCommonUserAttributes';
import { useAdministrator, useRestriction, useManager } from '../common/util/permissions';
import { useCatch } from '../reactHelper';
import useMapStyles from '../map/core/useMapStyles';
import { map } from '../map/core/MapView';
import useSettingsStyles from './common/useSettingsStyles';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import fetchOrThrow from '../common/util/fetchOrThrow';

const UserPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();

  const admin = useAdministrator();
  const manager = useManager();
  const fixedEmail = useRestriction('fixedEmail');

  const currentUser = useSelector((state) => state.session.user);
  const registrationEnabled = useSelector((state) => state.session.server.registration);
  const openIdForced = useSelector((state) => state.session.server.openIdForce);
  const totpEnable = useSelector((state) => state.session.server.attributes.totpEnable);
  const totpForce = useSelector((state) => state.session.server.attributes.totpForce);

  const mapStyles = useMapStyles();
  const commonUserAttributes = useCommonUserAttributes(t);
  const userAttributes = useUserAttributes(t);

  const { id } = useParams();

  const ensureFormStructure = (currentItem) => {
    if (!currentItem) return currentItem;
    return {
      ...currentItem,
      attributes: {
        ...currentItem.attributes,
        form: currentItem.attributes?.form || {}
      }
    };
  };

  const [item, setItem] = useState(() => ensureFormStructure(id === currentUser.id.toString() ? currentUser : null));
  const handleSetItem = (newItem) => {
    setItem(ensureFormStructure(newItem));
  };

  const updateFormField = (key, value) => {
    setItem((prev) => ensureFormStructure({
      ...prev,
      attributes: {
        ...prev.attributes,
        form: {
          ...prev.attributes?.form,
          [key]: value
        }
      }
    }));
  };

  const [deleteEmail, setDeleteEmail] = useState();
  const [deleteFailed, setDeleteFailed] = useState(false);

  const filterAttributes = (attributes) => {
    if (!attributes) return {};
    const filteredAttributes = {};
    Object.keys(attributes).forEach(key => {
      const value = attributes[key];
      if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
        filteredAttributes[key] = value;
      }
    });
    return filteredAttributes;
  };

  const updateAttributes = (newAttributes) => {
    const updatedAttributes = { ...newAttributes };
    if (item.attributes) {
      if (item.attributes.form) updatedAttributes.form = item.attributes.form;
    }
    setItem({ ...item, attributes: updatedAttributes });
  };

  const handleDelete = useCatch(async () => {
    if (deleteEmail === currentUser.email) {
      setDeleteFailed(false);
      await fetchOrThrow(`/api/users/${currentUser.id}`, { method: 'DELETE' });
      navigate('/login');
      dispatch(sessionActions.updateUser(null));
    } else {
      setDeleteFailed(true);
    }
  });

  const handleGenerateTotp = useCatch(async () => {
    const response = await fetchOrThrow('/api/users/totp', { method: 'POST' });
    setItem({ ...item, totpKey: await response.text() });
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const attribute = searchParams.get('attribute');

  useEffect(() => {
    if (item && attribute && !item.attributes.hasOwnProperty('attribute')) {
      const updatedAttributes = { ...item.attributes };
      updatedAttributes[attribute] = '';
      setItem({ ...item, attributes: updatedAttributes });

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('attribute');
      setSearchParams(newParams, { replace: true });
    }
  }, [item, searchParams, setSearchParams, attribute]);

  const onItemSaved = (result) => {
    if (result.id === currentUser.id) {
      dispatch(sessionActions.updateUser(result));
    }
  };

  const validate = () => item && item.name && item.email && (item.id || item.password) && (admin || !totpForce || item.totpKey);

  return (
    <EditItemView
      endpoint="users"
      item={item}
      setItem={handleSetItem}
      defaultItem={admin ? { deviceLimit: -1 } : {}}
      validate={validate}
      onItemSaved={onItemSaved}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'settingsUser']}
    >
      {item && (
        <>
          <Accordion defaultExpanded={!attribute}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon color="white" />
                <Typography variant="subtitle1">
                  {t('sharedRequired')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                width="100%"
                gap={2}
                sx={{
                  flexDirection: { xs: 'column', sm: 'row' } // Coluna em mobile, linha em desktop
                }}
              >
                {/* Campo Nome - ocupa mais espaço */}
                <Box flex={2} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item.name || ''}
                    onChange={(e) => setItem({ ...item, name: e.target.value })}
                    label={t('sharedName')}
                    required
                  />
                </Box>

                {/* Campo Email - ocupa mais espaço */}
                <Box flex={2} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item.email || ''}
                    onChange={(e) => setItem({ ...item, email: e.target.value })}
                    label={t('userEmail')}
                    disabled={fixedEmail && item.id === currentUser.id}
                    required
                  />
                </Box>

                {/* Campo Senha - ocupa menos espaço (apenas quando necessário) */}
                {!openIdForced && (
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      type="password"
                      onChange={(e) => setItem({ ...item, password: e.target.value })}
                      label={t('userPassword')}
                      required={!item.id}
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon color="white" />
                <Typography variant="subtitle1">
                  {t('sharedPreferences')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                {/* Primeira linha de campos */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  {/* Tipo de Conta */}
                  <Box flex={1} minWidth={0}>
                    <FormControl fullWidth>
                      <InputLabel>{t('sharedAccountType')}</InputLabel>
                      <Select
                        label={t('sharedAccountType')}
                        value={item?.attributes?.form?.accountType || ''}
                        onChange={(e) => updateFormField('accountType', e.target.value)}
                      >
                        <MenuItem value="individual">{t('sharedAccountIndividual')}</MenuItem>
                        <MenuItem value="company">{t('sharedAccountCompany')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* CPF/CNPJ */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.taxId || ''}
                      onChange={(event) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            taxId: event.target.value
                          }
                        }
                      })}
                      label={t('sharedCPFCNPJ')}
                    />
                  </Box>

                  {/* Telefone */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.phone || ''}
                      onChange={(e) => setItem({ ...item, phone: e.target.value })}
                      label={t('sharedPhone')}
                    />
                  </Box>
                </Box>

                {/* Segunda linha de campos */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  {/* WhatsApp */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.whatsapp || ''}
                      onChange={(event) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            whatsapp: event.target.value
                          }
                        }
                      })}
                      label={t('sharedWhatsapp')}
                    />
                  </Box>

                  {/* Telegram */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.telegramChatId || ''}
                      onChange={(event) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: {
                            ...item.attributes.form,
                            telegramChatId: event.target.value
                          }
                        }
                      })}
                      label={t('sharedTelegram')}
                    />
                  </Box>

                  {/* Mapa padrão */}
                  <Box flex={1} minWidth={0}>
                    <FormControl fullWidth>
                      <InputLabel>{t('mapDefault')}</InputLabel>
                      <Select
                        label={t('mapDefault')}
                        value={item.map || 'openFreeMap'}
                        onChange={(e) => setItem({ ...item, map: e.target.value })}
                      >
                        {mapStyles.filter((style) => style.available).map((style) => (
                          <MenuItem key={style.id} value={style.id}>
                            <Typography component="span">{style.title}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Terceira linha de campos */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  {/* Formato de coordenadas */}
                  <Box flex={1} minWidth={0}>
                    <FormControl fullWidth>
                      <InputLabel>{t('settingsCoordinateFormat')}</InputLabel>
                      <Select
                        label={t('settingsCoordinateFormat')}
                        value={item.coordinateFormat || 'dd'}
                        onChange={(e) => setItem({ ...item, coordinateFormat: e.target.value })}
                      >
                        <MenuItem value="dd">{t('sharedDecimalDegrees')}</MenuItem>
                        <MenuItem value="ddm">{t('sharedDegreesDecimalMinutes')}</MenuItem>
                        <MenuItem value="dms">{t('sharedDegreesMinutesSeconds')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Fuso horário */}
                  <Box flex={1} minWidth={0}>
                    <SelectField
                      fullWidth
                      value={item.attributes && item.attributes.timezone}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, timezone: e.target.value } })}
                      endpoint="/api/server/timezones"
                      keyGetter={(it) => it}
                      titleGetter={(it) => it}
                      label={t('sharedTimezone')}
                    />
                  </Box>

                  {/* Camada de POI */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.poiLayer || ''}
                      onChange={(e) => setItem({ ...item, poiLayer: e.target.value })}
                      label={t('mapPoiLayer')}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <MapIcon color="white" />
                <Typography variant="subtitle1">
                  {t('sharedAddress')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                width="100%"
                gap={2}
                sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
              >
                {/* Endereço (maior) */}
                <Box flex={3} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item?.attributes?.form?.address1 || ''}
                    onChange={(event) => setItem({
                      ...item,
                      attributes: {
                        ...item.attributes,
                        form: {
                          ...item.attributes.form,
                          address1: event.target.value
                        }
                      }
                    })}
                    label={t('sharedAddress')}
                  />
                </Box>

                {/* Bairro (maior) */}
                <Box flex={2} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item?.attributes?.form?.address2 || ''}
                    onChange={(event) => setItem({
                      ...item,
                      attributes: {
                        ...item.attributes,
                        form: {
                          ...item.attributes.form,
                          address2: event.target.value
                        }
                      }
                    })}
                    label={t('sharedNeighborhood')}
                  />
                </Box>

                {/* CEP */}
                <Box flex={1} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item?.attributes?.form?.postCode || ''}
                    onChange={(event) => setItem({
                      ...item,
                      attributes: {
                        ...item.attributes,
                        form: {
                          ...item.attributes.form,
                          postCode: event.target.value
                        }
                      }
                    })}
                    label={t('sharedPostalCode')}
                  />
                </Box>

                {/* Cidade */}
                <Box flex={1} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item?.attributes?.form?.city || ''}
                    onChange={(event) => setItem({
                      ...item,
                      attributes: {
                        ...item.attributes,
                        form: {
                          ...item.attributes.form,
                          city: event.target.value
                        }
                      }
                    })}
                    label={t('sharedCity')}
                  />
                </Box>

                {/* Estado (menor) */}
                <Box flex={0.8} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item?.attributes?.form?.state || ''}
                    onChange={(event) => setItem({
                      ...item,
                      attributes: {
                        ...item.attributes,
                        form: {
                          ...item.attributes.form,
                          state: event.target.value
                        }
                      }
                    })}
                    label={t('sharedState')}
                  />
                </Box>

                {/* País (menor) */}
                <Box flex={0.8} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item?.attributes?.form?.country || ''}
                    onChange={(event) => setItem({
                      ...item,
                      attributes: {
                        ...item.attributes,
                        form: {
                          ...item.attributes.form,
                          country: event.target.value
                        }
                      }
                    })}
                    label={t('sharedCountry')}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/*           //MOD */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
             <Box display="flex" alignItems="center" gap={1}>
                                <LocationOnIcon color="white" />
              <Typography variant="subtitle1">
                {t('sharedLocation')}
              </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                type="number"
                value={item.latitude || 0}
                onChange={(e) => setItem({ ...item, latitude: Number(e.target.value) })}
                label={t('positionLatitude')}
              />
              <TextField
                type="number"
                value={item.longitude || 0}
                onChange={(e) => setItem({ ...item, longitude: Number(e.target.value) })}
                label={t('positionLongitude')}
              />
              <TextField
                type="number"
                value={item.zoom || 0}
                onChange={(e) => setItem({ ...item, zoom: Number(e.target.value) })}
                label={t('serverZoom')}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const { lng, lat } = map.getCenter();
                  setItem({
                    ...item,
                    latitude: Number(lat.toFixed(6)),
                    longitude: Number(lng.toFixed(6)),
                    zoom: Number(map.getZoom().toFixed(1)),
                  });
                }}
              >
                {t('mapCurrentLocation')}
              </Button>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
             <Box display="flex" alignItems="center" gap={1}>
                                <SecurityIcon color="white" />
              <Typography variant="subtitle1">
                {t('sharedPermissions')}
              </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                label={t('userExpirationTime')}
                type="date"
                value={item.expirationTime ? item.expirationTime.split('T')[0] : '2099-01-01'}
                onChange={(e) => {
                  if (e.target.value) {
                    setItem({ ...item, expirationTime: new Date(e.target.value).toISOString() });
                  }
                }}
                disabled={!manager}
              />
              <TextField
                type="number"
                value={item.deviceLimit || 0}
                onChange={(e) => setItem({ ...item, deviceLimit: Number(e.target.value) })}
                label={t('userDeviceLimit')}
                disabled={!admin}
              />
              <TextField
                type="number"
                value={item.userLimit || 0}
                onChange={(e) => setItem({ ...item, userLimit: Number(e.target.value) })}
                label={t('userUserLimit')}
                disabled={!admin}
              />
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={item.disabled} onChange={(e) => setItem({ ...item, disabled: e.target.checked })} />}
                  label={t('sharedDisabled')}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={<Checkbox checked={item.administrator} onChange={(e) => setItem({ ...item, administrator: e.target.checked })} />}
                  label={t('userAdmin')}
                  disabled={!admin}
                />
                <FormControlLabel
                  control={<Checkbox checked={item.readonly} onChange={(e) => setItem({ ...item, readonly: e.target.checked })} />}
                  label={t('serverReadonly')}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={<Checkbox checked={item.deviceReadonly} onChange={(e) => setItem({ ...item, deviceReadonly: e.target.checked })} />}
                  label={t('userDeviceReadonly')}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={<Checkbox checked={item.limitCommands} onChange={(e) => setItem({ ...item, limitCommands: e.target.checked })} />}
                  label={t('userLimitCommands')}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={<Checkbox checked={item.disableReports} onChange={(e) => setItem({ ...item, disableReports: e.target.checked })} />}
                  label={t('userDisableReports')}
                  disabled={!manager}
                />
                <FormControlLabel
                  control={<Checkbox checked={item.fixedEmail} onChange={(e) => setItem({ ...item, fixedEmail: e.target.checked })} />}
                  label={t('userFixedEmail')}
                  disabled={!manager}
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>
          <EditAttributesAccordion
            attribute={attribute}
            attributes={filterAttributes(item.attributes)}
            setAttributes={updateAttributes}
            definitions={{ ...commonUserAttributes, ...userAttributes }}
            focusAttribute={attribute}
          />
          {registrationEnabled && item.id === currentUser.id && !manager && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" color="error">
                  {t('userDeleteAccount')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <TextField
                  value={deleteEmail}
                  onChange={(e) => setDeleteEmail(e.target.value)}
                  label={t('userEmail')}
                  error={deleteFailed}
                />
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  startIcon={<DeleteForeverIcon />}
                >
                  {t('userDeleteAccount')}
                </Button>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </EditItemView>
  );
};

export default UserPage;
